import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { btnP, btnSec, inpS, rad, sp, type } from "../../constants/styles";
import Ic from "../common/Ic";
import Fld from "../common/Fld";
import { LANGUAGES } from "../../i18n";
import TermsView from "../common/TermsView";
import PrivacyView from "../common/PrivacyView";
import ColdStartLoader from "../common/ColdStartLoader";
import { TERMS_VERSION } from "../../utils/terms";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";

const GSI_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Capacitor native-platform check. We can't use Capacitor.isNativePlatform()
// directly here (would crash SSR / pure-web tests). Read the runtime global.
const _isCapacitorNative = typeof window !== "undefined"
    && window.Capacitor
    && typeof window.Capacitor.isNativePlatform === "function"
    && window.Capacitor.isNativePlatform();

// Phone input with a pinned +91 country prefix (from the approved
// design concept). Mirrors Fld's label treatment; the bound value and
// validation are untouched — the prefix is purely visual, so pasting
// a full "+91…" number still normalizes server-side exactly as before.
const PhoneFld = ({ l, v, set, h }) => {
  const base = inpS(h);
  return <>
    <label style={{ color: h.ts || h.sc, fontSize: 12, fontWeight: 600, display: "block", marginBottom: sp.xs + 1, letterSpacing: "0.3px", textTransform: "uppercase" }}>{l}</label>
    <div style={{ position: "relative", marginBottom: base.marginBottom }}>
      <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: h.ts || h.sc, fontSize: 14, fontWeight: 700, pointerEvents: "none" }}>+91</span>
      <input value={v} onChange={(e) => set(e.target.value)} type="tel" inputMode="tel" placeholder="98765 43210" style={{ ...base, paddingLeft: 52, marginBottom: 0 }} />
    </div>
  </>;
};

// Concept-styled field for the auth screen (design-concept-final.html
// ".f" block): tiny uppercase letterspaced label, white (dark: dim)
// rounded-14 input, role-colored focus ring, optional pinned "+91"
// prefix with a divider, optional password eye toggle. Visual only —
// values flow through `set` exactly like Fld.
const CFld = ({ l, v, set, type, ph, pre, h, dk, ac }) => {
  const [show, setShow] = useState(false);
  const isPwd = type === "password";
  const line = dk ? "rgba(255,255,255,0.12)" : "#e7eaf6";
  const mute = dk ? "#8b93a7" : "#98a0b6";
  return <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontWeight: 800, fontSize: 10, letterSpacing: "1.6px", textTransform: "uppercase", color: mute, marginBottom: 7 }}>{l}</label>
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      {pre && <span style={{ position: "absolute", left: 14, fontWeight: 700, fontSize: 14, color: dk ? "#aab2c5" : "#5b6478", borderRight: `1.5px solid ${line}`, padding: "2px 10px 2px 0", pointerEvents: "none", zIndex: 1 }}>{pre}</span>}
      <input
        value={v} onChange={(e) => set(e.target.value)}
        type={isPwd ? (show ? "text" : "password") : (type || "text")}
        inputMode={type === "tel" ? "tel" : undefined}
        placeholder={ph || ""}
        autoComplete={isPwd ? (show ? "off" : "current-password") : (type === "tel" ? "tel" : undefined)}
        onFocus={(e) => { e.target.style.borderColor = ac; e.target.style.boxShadow = `0 0 0 4px ${ac}24`; }}
        onBlur={(e) => { e.target.style.borderColor = line; e.target.style.boxShadow = "none"; }}
        style={{
          width: "100%", padding: "14.5px 16px",
          paddingLeft: pre ? 64 : 16, paddingRight: isPwd ? 44 : 16,
          borderRadius: 14, fontSize: 15, fontWeight: 600, color: h.tx,
          background: dk ? "rgba(255,255,255,0.05)" : "#fff",
          border: `1.5px solid ${line}`, outline: "none", boxSizing: "border-box",
          transition: "border-color 0.25s, box-shadow 0.25s", fontFamily: "inherit",
          colorScheme: dk ? "dark" : "light",
        }}
      />
      {isPwd && <button type="button" onClick={() => setShow(s => !s)}
        aria-label={show ? "Hide password" : "Show password"} aria-pressed={show}
        style={{ position: "absolute", right: 12, border: "none", background: "none", cursor: "pointer", fontSize: 16, color: mute, padding: 4 }}>
        <span aria-hidden="true">{show ? "🙈" : "👁"}</span>
      </button>}
    </div>
  </div>;
};

export default function LoginPage() {
  const { t, h, lang, setLang, dk, setDk, role, setRole, setLoggedIn, setCurrUserId, setPublicPage, setOrgId } = useContext(Ctx);
  const [ph, setPh] = useState("");
  const [pwd, setPwd] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [skill, setSkill] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [mode, setMode] = useState("login"); // login | signup | googleRole
  const [loading, setLoading] = useState(false);
  const [loginErr, setLoginErr] = useState("");
  // ── Forgot Password (OTP flow) ──
  // Step 1: phone → server sends OTP via SMS (email fallback)
  // Step 2: enter OTP + new password → server verifies + resets
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=email, 2=otp+newPwd
  const [forgotPh, setForgotPh] = useState("");  // holds the account EMAIL (phone/SMS OTP parked for later)
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPwd, setForgotNewPwd] = useState("");
  const [forgotConfirm, setForgotConfirm] = useState("");
  const [forgotErr, setForgotErr] = useState("");
  const [forgotInfo, setForgotInfo] = useState("");  // generic confirmation banner
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0); // seconds remaining before resend allowed
  // Tick down resend timer
  React.useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);
  // Terms acceptance — required before signup. Hard-blocks the signup
  // button until checked. The accepted version is sent to /api/signup
  // so the server can record consent in `terms_acceptances` (legal audit).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [tenancies, setTenancies] = useState(null); // multi-org picker
  const [tenancyPh, setTenancyPh] = useState(""); // store phone for org selection
  const [tenancyPwd, setTenancyPwd] = useState(""); // store pwd for org selection
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleCredential, setGoogleCredential] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const googleBtnRef = useRef(null);
  const gsiLoaded = useRef(false);

  const completeLogin = useCallback((resRole, res) => {
    localStorage.setItem("loggedIn", "true");
    localStorage.setItem("role", resRole);
    if (res.orgId) localStorage.setItem("orgId", res.orgId);
    if (res.user?.id) { localStorage.setItem("currUserId", res.user.id); setCurrUserId(res.user.id); }
    localStorage.setItem("googleUser", JSON.stringify(res.user));
    if (setOrgId && res.orgId) setOrgId(res.orgId);
    setRole(resRole);
    setLoggedIn(true);
  }, [setCurrUserId, setRole, setLoggedIn, setOrgId]);

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) return;
    setGoogleLoading(true);
    setLoginErr("");
    try {
      const res = await api.googleLogin(response.credential);
      if (res.success && res.needsRole) {
        // New Google user — needs to pick a role and provide phone
        setGoogleCredential(response.credential);
        setGoogleUser(res.user);
        setName(res.user.name || "");
        setMode("googleRole");
        setRole(null);
      } else if (res.success && res.role) {
        // Existing user with a role — auto-login
        completeLogin(res.role, res);
      } else {
        setLoginErr(res.message || "Google sign-in failed");
      }
    } catch (err) {
      setLoginErr("Google sign-in failed: " + err.message);
    } finally {
      setGoogleLoading(false);
    }
  }, [completeLogin]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || gsiLoaded.current) return;
    // GIS (the web Google Sign-In script) refuses to render inside a
    // Capacitor WebView — Google blocks WebView origins as a phishing
    // mitigation. Skip the whole GSI load on native and let the
    // Capacitor button (rendered separately below) handle it.
    if (_isCapacitorNative) {
      // Pre-init the native plugin once. Subsequent signIn() calls are fast.
      GoogleAuth.initialize({
        clientId: GOOGLE_CLIENT_ID,
        scopes: ["profile", "email"],
        grantOfflineAccess: true,
      });
      gsiLoaded.current = true;
      return;
    }
    const loadGsi = () => {
      if (window.google?.accounts?.id) { initGsi(); return; }
      if (document.querySelector(`script[src="${GSI_SRC}"]`)) return;
      const script = document.createElement("script");
      script.src = GSI_SRC; script.async = true; script.defer = true;
      script.onload = () => initGsi();
      document.head.appendChild(script);
    };
    const initGsi = () => {
      if (gsiLoaded.current || !window.google?.accounts?.id) return;
      gsiLoaded.current = true;
      window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleResponse, auto_select: false, cancel_on_tap_outside: true });
      renderGoogleButton();
    };
    loadGsi();
  }, [handleGoogleResponse]);

  useEffect(() => {
    if (_isCapacitorNative) return;          // No GIS button on native
    // The ref div lives on the landing AND on the auth screen (concept
    // design) — re-render the GIS button wherever it is currently mounted.
    if (gsiLoaded.current && googleBtnRef.current) renderGoogleButton();
  }, [dk, role, mode]);

  // Native Google Sign-In handler — opens the system Google account picker
  // on Android / iOS via the Capacitor plugin, gets a Google ID token,
  // then sends it through the same /api/auth/google endpoint as web.
  const handleNativeGoogleSignIn = async () => {
    if (!GOOGLE_CLIENT_ID) {
      setLoginErr("Google Sign-In not configured");
      return;
    }
    setGoogleLoading(true);
    setLoginErr("");
    try {
      const result = await GoogleAuth.signIn();
      // The plugin returns an authentication object — `idToken` is what
      // our backend's verifyIdToken() expects (same audience as web GIS).
      const idToken = result?.authentication?.idToken;
      if (!idToken) throw new Error("No idToken returned from Google");
      await handleGoogleResponse({ credential: idToken });
    } catch (err) {
      // User cancellation surfaces as code 12501 / "popup_closed" —
      // suppress the error message in that case.
      const msg = (err && (err.message || err.errorMessage)) || "";
      if (/12501|cancel|closed/i.test(msg)) {
        // Silent — user cancelled.
      } else {
        setLoginErr("Google sign-in failed: " + msg);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const renderGoogleButton = () => {
    if (!googleBtnRef.current || !window.google?.accounts?.id) return;
    googleBtnRef.current.innerHTML = "";
    window.google.accounts.id.renderButton(googleBtnRef.current, { type: "standard", theme: dk ? "filled_black" : "outline", size: "large", width: googleBtnRef.current.offsetWidth || 340, text: "continue_with", shape: "pill", logo_alignment: "left" });
  };

  const roleIcons = { landlord: "shield", tenant: "users", service: "wrench", admin: "gear" };
  // Per-role accents so the bento landing cards have visual variety
  // (matches the reference "Select Your Path" design language where
  // each tier card pulls from a slightly different tone). Falls back
  // to the brand accent if a theme color is missing.
  const roleColors = {
    landlord: h.ac,
    tenant: h.gn || h.ac,
    service: h.pp || h.wn || h.ac,
    admin: h.pp || h.wn,
  };
  const roleLabels = { landlord: t.landlord, tenant: t.tenant, service: t.servicePerson, admin: t.superAdmin || "Super Admin" };
  // Short value-prop subtitle + CTA chip text per role. Hardcoded
  // English because t.* doesn't have these keys yet and the marketing
  // copy reads well unchanged across the locale pivots we care about.
  const roleDescriptions = {
    landlord: "Oversee your portfolio with clarity, real-time insights, and effortless rent collection.",
    tenant:   "Pay rent, raise maintenance, and access your agreement — all in one place.",
    service:  "Pick up assigned jobs, log work, and get paid through a streamlined technician portal.",
  };
  const roleCTAs = {
    landlord: "Manage Portfolio",
    tenant:   "Enter Residence",
    service:  "Technician Portal",
  };
  // Role gradient pairs — identical to the landing role cards so the
  // path picked on the home screen keeps its color identity through
  // login/signup (approved concept: design-concept-final.html).
  const roleGrads = {
    landlord: ["#60a5fa", "#4f46e5"],
    tenant: ["#34d399", "#059669"],
    service: ["#a78bfa", "#7c3aed"],
    admin: ["#64748b", "#334155"],
  };
  const btnRole = (r) => {
    const [ga, gb] = roleGrads[r] || roleGrads.landlord;
    return { ...btnP(h), background: `linear-gradient(135deg, ${ga}, ${gb})`, boxShadow: `0 10px 24px ${gb}40` };
  };

  const resetForm = () => { setPh(""); setPwd(""); setName(""); setOrgName(""); setSkill(""); setConfirmPwd(""); setLoginErr(""); };
  const switchMode = (m) => { setMode(m); resetForm(); };

  const handleLogin = async () => {
    if (ph.length < 10) { setLoginErr(t.validPhone || "Enter valid phone"); return; }
    if (!pwd) { setLoginErr(t.enterPwd || "Enter password"); return; }
    setLoading(true); setLoginErr("");
    try {
      const res = await api.login(role, ph, pwd);
      if (res.success && res.needsOrgSelection) {
        // Multi-org tenant — show org picker
        setTenancies(res.tenancies);
        setTenancyPh(ph);
        setTenancyPwd(pwd);
      } else if (res.success) {
        localStorage.setItem("loggedIn", "true");
        localStorage.setItem("role", role);
        if (res.orgId) localStorage.setItem("orgId", res.orgId);
        if (role === "tenant" || role === "service") {
          localStorage.setItem("currUserId", res.user.id);
          setCurrUserId(res.user.id);
        } else if (role === "admin") {
          localStorage.setItem("currUserId", "super_admin");
          setCurrUserId("super_admin");
        } else if (role === "landlord" && res.user?.id) {
          localStorage.setItem("currUserId", res.user.id);
          setCurrUserId(res.user.id);
        }
        if (setOrgId && res.orgId) setOrgId(res.orgId);
        setLoggedIn(true);
      } else { setLoginErr(res.message || t.loginFail); }
    } catch (err) { setLoginErr(t.loginFail + ": " + err.message); }
    finally { setLoading(false); }
  };

  const handleSignup = async () => {
    if (!name.trim()) { setLoginErr("Name is required"); return; }
    if (!signupEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupEmail.trim())) { setLoginErr("Enter a valid email (needed to recover your password)"); return; }
    if (ph.length < 10) { setLoginErr(t.validPhone || "Enter valid phone"); return; }
    if (pwd.length < 4) { setLoginErr("Password must be at least 4 characters"); return; }
    if (pwd !== confirmPwd) { setLoginErr("Passwords don't match"); return; }
    if (role === "landlord" && !orgName.trim()) { setLoginErr("Organization name is required"); return; }
    if (!termsAccepted) { setLoginErr("Please accept the Terms & Conditions to continue"); return; }
    setLoading(true); setLoginErr("");
    try {
      const res = await api.signup(role, ph, pwd, name, orgName, skill, TERMS_VERSION, signupEmail.trim());
      if (res.success) {
        localStorage.setItem("loggedIn", "true");
        localStorage.setItem("role", role);
        if (res.orgId) localStorage.setItem("orgId", res.orgId);
        if (res.user?.id) {
          localStorage.setItem("currUserId", res.user.id);
          setCurrUserId(res.user.id);
        }
        if (setOrgId && res.orgId) setOrgId(res.orgId);
        setLoggedIn(true);
      } else { setLoginErr(res.message || "Signup failed"); }
    } catch (err) { setLoginErr("Signup failed: " + err.message); }
    finally { setLoading(false); }
  };

  const handleGoogleSignup = async (selectedRole) => {
    if (!ph || ph.length < 10) { setLoginErr(t.validPhone || "Enter valid phone"); return; }
    if (selectedRole === "landlord" && !orgName.trim()) { setLoginErr("Organization name is required"); return; }
    if (!termsAccepted) { setLoginErr("Please accept the Terms & Conditions to continue"); return; }
    setLoading(true); setLoginErr("");
    try {
      const res = await api.googleLogin(googleCredential, selectedRole, ph, orgName, skill, name, TERMS_VERSION);
      if (res.success && res.role) {
        completeLogin(res.role, res);
      } else { setLoginErr(res.message || "Signup failed"); }
    } catch (err) { setLoginErr("Signup failed: " + err.message); }
    finally { setLoading(false); }
  };

  const tabS = (active) => ({
    flex: 1, padding: `${sp.sm}px 0`, borderRadius: rad.pill, border: "none",
    background: active ? h.ac : "transparent", color: active ? "#fff" : h.ts,
    fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 0.2s",
  });

  // ────────────────────────────────────────────────────────────────
  // LuxeRent-reference landing screen.
  //
  // Renders ONLY when the user hasn't yet picked a role and isn't
  // mid-flow (no Google role selection, no tenancies picker, no
  // loading). All other branches keep the existing layout below so
  // the login form / signup / Google role flow / forgot password
  // continue to work unchanged.
  //
  // Design parity points with the reference HTML the user shared:
  //   - Manrope display font (loaded via index.html)
  //   - Material Symbols Outlined icons (loaded via index.html)
  //   - Off-white "surface" bg #f9f9fe (light) / dark equivalent (dk)
  //   - Two large blur orbs (secondary/red top-right, primary/black
  //     bottom-left) — pinned with position:fixed so they read as
  //     atmosphere not content
  //   - Radial dot grid background at 20% opacity
  //   - Top nav bar: white/70 glass + backdrop-blur, hamburger left,
  //     brand center-left, language + theme circle right (the
  //     reference's avatar slot)
  //   - "Select Your Path" hero: display-lg 34/41/-0.5/800 + body-md
  //   - Bento grid: 1-col under 768px, 3-col above (via auto-fit
  //     minmax — same responsive behavior as Tailwind's md:grid-cols-3)
  //   - Per-card: neumorphic-lift glass-card border, white icon tile,
  //     Material Symbols icon, headline-md role name, body-sm
  //     description, label-caps uppercase CTA + arrow_forward_ios
  //   - Secondary section: "Already have an account?" + black pill CTA
  //   - Super Admin + Google Sign-In + Version line in the footer
  const isLandingScreen = !role && !tenancies && mode !== "googleRole" && !loading && !googleLoading;
  if (isLandingScreen) {
    // "Signature" landing palette — the approved concept (see
    // design-concept-final.html): brand-blue gradient hero with
    // floating glass shapes, role cards in the app's own dashboard
    // gradient language, auth one tap away. Light sheet; dark mode
    // dims the sheet while the hero stays brand blue.
    const sheetBg       = dk ? "#0e1016" : "#f5f7ff";
    const onSurface     = dk ? "#f2f3f8" : "#131722";
    const onSurfaceVar  = dk ? "#a6abbd" : "#5b6478";
    const onSurfaceMute = dk ? "#787e92" : "#98a0b6";
    const secondary     = "#e11d48";          // error accent (login failures)
    const glassBorder   = dk ? "rgba(255,255,255,0.12)" : "rgba(23,30,60,0.08)";
    const cardBg        = dk ? "#171a23" : "#ffffff";

    // Per-role config — the app's dashboard gradient pairs, phrased the
    // way users think of themselves.
    const cards = [
      {
        key: "landlord", icon: "real_estate_agent",
        title: "I'm a Landlord",
        desc: "Collect rent, manage properties & tenants",
        grad: ["#60a5fa", "#4f46e5"],
      },
      {
        key: "tenant", icon: "home_pin",
        title: "I'm a Tenant",
        desc: "Pay rent, raise requests, view your lease",
        grad: ["#34d399", "#059669"],
      },
      {
        key: "service", icon: "engineering",
        title: "I'm a Service Person",
        desc: "Get jobs, track charges & earnings",
        grad: ["#a78bfa", "#7c3aed"],
      },
    ];

    return (
      <div className="animate-fadeIn" style={{
        position: "relative",
        minHeight: "100vh", width: "100%",
        background: sheetBg,
        color: onSurface,
        fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
        overflowX: "hidden",
      }}>
        {/* Entrance + float keyframes — CSS only, no animation libs. */}
        <style>{`
          @keyframes lpUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
          .lp-up { animation: lpUp .6s cubic-bezier(.22,.8,.3,1) both; }
          @keyframes lpDrift { 0%,100% { translate: 0 0; } 50% { translate: 0 -14px; } }
          @media (prefers-reduced-motion: reduce) { .lp-up, [data-shape] { animation: none !important; } }
        `}</style>

        {/* ── Brand-blue hero: floating glass shapes + pointer parallax ── */}
        <div
          onPointerMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const nx = (e.clientX - r.left) / r.width - 0.5;
            const ny = (e.clientY - r.top) / r.height - 0.5;
            e.currentTarget.querySelectorAll("[data-shape]").forEach((s) => {
              const d = +s.dataset.depth || 16;
              s.style.translate = `${nx * d}px ${ny * d}px`;
            });
          }}
          style={{
            position: "relative", overflow: "hidden",
            padding: "18px 22px 24px",
            background:
              "radial-gradient(420px 260px at 90% -40px, rgba(99,102,241,0.32) 0%, transparent 65%)," +
              "radial-gradient(380px 300px at -60px 90%, rgba(52,211,153,0.20) 0%, transparent 60%)," +
              "linear-gradient(165deg, #2f6df6 0%, #3b82f6 38%, #6366f1 100%)",
            borderRadius: "0 0 38px 38px",
            boxShadow: "0 24px 50px rgba(59,130,246,0.30)",
          }}>
          {/* floating glass shapes */}
          {[
            { e: "🏠", s: 84, top: 86, right: 24, d: 18, delay: "0s", fs: 26 },
            { e: "🔑", s: 54, top: 30, right: 128, d: 34, delay: "-3s", fs: 19 },
            { e: "₹", s: 62, top: 188, right: 88, d: 26, delay: "-5.5s", fs: 22 },
          ].map((sh, i) => (
            <div key={i} data-shape data-depth={sh.d} style={{
              position: "absolute", width: sh.s, height: sh.s,
              top: sh.top, right: sh.right,
              borderRadius: "26%",
              background: "linear-gradient(135deg, rgba(255,255,255,0.30), rgba(255,255,255,0.06))",
              border: "1px solid rgba(255,255,255,0.40)",
              backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
              boxShadow: "0 18px 44px rgba(13,40,120,0.25), inset 0 1px 0 rgba(255,255,255,0.5)",
              display: "grid", placeItems: "center", fontSize: sh.fs,
              animation: `lpDrift 8.5s ease-in-out ${sh.delay} infinite`,
            }}>{sh.e}</div>
          ))}
          <div data-shape data-depth={10} style={{
            position: "absolute", width: 120, height: 120, left: -38, top: 40,
            borderRadius: "50%", border: "12px solid rgba(255,255,255,0.18)",
            animation: "lpDrift 8.5s ease-in-out -7s infinite",
          }} />

          {/* top bar: brand + language + theme (inside the hero) */}
          <div className="lp-up" style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: "-0.3px" }}>
              <span style={{ width: 36, height: 36, borderRadius: 11, overflow: "hidden", display: "grid", placeItems: "center", background: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.55)" }}>
                <img src="/logo192.png" alt="ProRentz" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </span>
              {t.app || "ProRentz"}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={() => { const codes = LANGUAGES.map(l => l.code); const next = codes[(codes.indexOf(lang) + 1) % codes.length]; setLang(next); }}
                style={{
                  border: "1px solid rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.18)",
                  color: "#fff", padding: "7px 12px", borderRadius: 999, cursor: "pointer",
                  fontSize: 11, fontWeight: 700, backdropFilter: "blur(8px)", fontFamily: "inherit",
                }}
              >{(LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]).native} ▾</button>
              <button
                onClick={() => setDk(!dk)}
                aria-label="Toggle theme"
                style={{
                  width: 30, height: 30, borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.18)",
                  color: "#fff", cursor: "pointer", fontSize: 13, backdropFilter: "blur(8px)",
                }}
              >{dk ? "☀" : "☾"}</button>
            </div>
          </div>

          {/* hero copy + trust chips */}
          <div style={{ position: "relative", zIndex: 2, marginTop: 32, color: "#fff" }}>
            <div className="lp-up" style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.92, marginBottom: 8, animationDelay: ".06s" }}>Namaste 👋</div>
            <h1 className="lp-up" style={{ fontSize: 31, lineHeight: 1.16, letterSpacing: "-1.1px", fontWeight: 800, margin: 0, animationDelay: ".12s" }}>
              Renting made<br /><span style={{ color: "#ffe48a" }}>simple & secure.</span>
            </h1>
            <p className="lp-up" style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,255,255,0.85)", maxWidth: 300, fontWeight: 500, animationDelay: ".18s" }}>
              Rent, maintenance, leases and tenants — everything in one app, in your language.
            </p>
            <div className="lp-up" style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap", animationDelay: ".24s" }}>
              {["🏢 12k+ properties", "₹ 4.2Cr processed", "⭐ 4.9 rated"].map((tc) => (
                <span key={tc} style={{ fontSize: 10.5, fontWeight: 700, color: "#fff", padding: "6px 11px", borderRadius: 999, background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.30)", backdropFilter: "blur(6px)" }}>{tc}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Main column — role cards overlap the hero, then Google,
            browse and the footer. */}
        <main style={{
          position: "relative", zIndex: 3,
          padding: "0 20px 28px",
          maxWidth: 470, margin: "0 auto",
          display: "flex", flexDirection: "column", alignItems: "center",
        }}>

          {/* Role cards — gradient language, one tap → sign-in. They sit
              below the hero with a clear gap (was a negative-margin overlap). */}
          <div style={{ width: "100%", marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {cards.map((c, i) => (
              <button
                key={c.key}
                className="lp-up"
                onClick={() => { setRole(c.key); resetForm(); setMode("login"); }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px) scale(1.015)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
                style={{
                  display: "flex", alignItems: "center", gap: 14, textAlign: "left",
                  border: "none", borderRadius: 22, padding: "17px 18px", cursor: "pointer",
                  background: `linear-gradient(135deg, ${c.grad[0]} 0%, ${c.grad[1]} 100%)`,
                  boxShadow: `0 14px 30px ${c.grad[1]}50`,
                  color: "#fff", fontFamily: "inherit",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  animationDelay: `${0.28 + i * 0.07}s`,
                }}
              >
                <span style={{
                  width: 48, height: 48, borderRadius: 15, display: "grid", placeItems: "center",
                  flexShrink: 0, background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.35)",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 24, color: "#fff", fontVariationSettings: "'FILL' 1" }}>{c.icon}</span>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ display: "block", fontSize: 16.5, letterSpacing: "-0.2px", fontWeight: 800 }}>{c.title}</b>
                  <span style={{ display: "block", fontSize: 11.5, marginTop: 2, color: "rgba(255,255,255,0.85)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.desc}</span>
                </span>
                <span style={{
                  width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center",
                  fontWeight: 800, background: "rgba(255,255,255,0.92)", color: c.grad[1],
                  flexShrink: 0, fontSize: 15, boxShadow: "0 6px 14px rgba(13,30,80,0.18)",
                }}>→</span>
              </button>
            ))}
          </div>

          {/* Browse without an account — the public marketplace. */}
          <button
            className="lp-up"
            onClick={() => setPublicPage("browse")}
            style={{
              width: "100%", marginTop: 16, padding: 14, borderRadius: 16,
              cursor: "pointer", background: cardBg, color: onSurface,
              border: `1.5px solid ${glassBorder}`,
              fontSize: 14, fontWeight: 700, fontFamily: "inherit",
              boxShadow: "0 8px 22px rgba(23,30,60,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              animationDelay: ".5s",
            }}
          >🔍 Browse properties without an account</button>

          {/* Secondary auth — Google sign-in (kept from original flow).
              Less prominent than the role cards since it's a re-entry
              path, not the primary onboarding. */}
          {GOOGLE_CLIENT_ID && (
            <div style={{ marginTop: 32, width: "100%", maxWidth: 360 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 0 16px" }}>
                <div style={{ flex: 1, height: 1, background: glassBorder }} />
                <span style={{
                  color: onSurfaceMute, fontSize: 11,
                  textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600,
                }}>or sign in with</span>
                <div style={{ flex: 1, height: 1, background: glassBorder }} />
              </div>
              {_isCapacitorNative ? (
                <button
                  onClick={handleNativeGoogleSignIn}
                  style={{
                    width: "100%", padding: "12px 16px", borderRadius: 999,
                    border: `1px solid ${glassBorder}`,
                    background: dk ? "rgba(255,255,255,0.04)" : "#ffffff",
                    color: dk ? "#e8eaed" : "#3c4043",
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
                    boxShadow: dk ? "none" : "0 1px 2px rgba(0,0,0,0.05)",
                    fontFamily: "inherit",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M16.51 8.18c0-.69-.06-1.36-.18-2H9v3.78h4.21c-.18.99-.74 1.83-1.58 2.39v1.99h2.55c1.49-1.37 2.33-3.39 2.33-5.16z"/>
                    <path fill="#34A853" d="M9 17c2.13 0 3.92-.71 5.22-1.92l-2.55-1.99c-.71.48-1.61.76-2.67.76-2.06 0-3.81-1.39-4.43-3.27H1.94v2.05A8 8 0 009 17z"/>
                    <path fill="#FBBC05" d="M4.57 10.58A4.81 4.81 0 014.32 9c0-.55.09-1.09.25-1.58V5.37H1.94A8 8 0 001 9c0 1.29.31 2.51.94 3.63l2.63-2.05z"/>
                    <path fill="#EA4335" d="M9 3.58c1.16 0 2.21.4 3.03 1.18l2.27-2.27C12.91.93 11.13 0 9 0A8 8 0 001.94 5.37l2.63 2.05C5.19 4.97 6.94 3.58 9 3.58z"/>
                  </svg>
                  Continue with Google
                </button>
              ) : (
                <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", width: "100%" }} />
              )}
              {loginErr && <div style={{
                background: `${secondary}10`, color: secondary,
                padding: "10px 14px", borderRadius: 12,
                fontSize: 13, fontWeight: 500, marginTop: 16,
                textAlign: "center", border: `1px solid ${secondary}20`,
              }}>{loginErr}</div>}
            </div>
          )}

          {/* Footer — Super Admin link + Version, tiny + dim */}
          <div style={{
            marginTop: 48, display: "flex", alignItems: "center", gap: 12,
            color: onSurfaceMute, fontSize: 11,
            letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 600,
          }}>
            <button
              onClick={() => { setRole("admin"); resetForm(); setMode("login"); }}
              style={{
                background: "none", border: "none", color: "inherit",
                cursor: "pointer", fontSize: "inherit", fontWeight: "inherit",
                letterSpacing: "inherit", textTransform: "inherit",
                padding: 0, fontFamily: "inherit",
              }}
            >Super Admin</button>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ opacity: 0.6 }}>Version 4.0</span>
          </div>
        </main>

        {/* Modals — kept available even on the landing in case the
            user opened terms/privacy from somewhere. */}
        {showTermsModal && <TermsView onClose={() => setShowTermsModal(false)} />}
        {showPrivacyModal && <PrivacyView onClose={() => setShowPrivacyModal(false)} />}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  // Auth screen — faithful port of design-concept-final.html's #auth
  // view: full-bleed role-gradient band on top (back pill, mini brand,
  // 52px role tile + name + hint, glass role-switch chips), then a
  // flat sheet with the segmented Login/Sign Up control, concept-styled
  // fields (+91 phone, eye toggle, role focus ring), role-gradient CTA
  // and the Google block. All real auth logic is unchanged — this is
  // the same forms/handlers re-skinned.
  const isAuthScreen = role && !tenancies && mode !== "googleRole";
  if (isAuthScreen) {
    if (loading || googleLoading) {
      return <ColdStartLoader h={h} fullscreen label={mode === "signup" ? "Creating account" : "Authenticating"} />;
    }
    const [ga, gb] = roleGrads[role] || roleGrads.landlord;
    const isAdmin = role === "admin";
    const sheetBg = dk ? "#0e1016" : "#f5f7ff";
    const lineC = dk ? "rgba(255,255,255,0.12)" : "#e7eaf6";
    const muteC = dk ? "#8b93a7" : "#98a0b6";
    const dimC = dk ? "#aab2c5" : "#5b6478";
    const segS = (active) => ({
      flex: 1, padding: 11, border: "none", borderRadius: 11, cursor: "pointer",
      fontWeight: 800, fontSize: 13.5, fontFamily: "inherit", transition: "all 0.25s",
      color: active ? (dk ? "#fff" : "#131722") : dimC,
      background: active ? (dk ? "#232838" : "#fff") : "transparent",
      boxShadow: active ? "0 4px 12px rgba(23,30,60,0.10)" : "none",
    });
    const ctaS = {
      width: "100%", padding: 16, border: "none", borderRadius: 16, cursor: "pointer",
      color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: "0.2px", fontFamily: "inherit",
      background: `linear-gradient(135deg, ${ga} 0%, ${gb} 100%)`,
      boxShadow: `0 14px 30px ${gb}61`,
      transition: "transform 0.22s cubic-bezier(.2,.9,.3,1.4), box-shadow 0.25s, background 0.4s",
    };
    const errBox = { background: `${h.rd}10`, color: h.rd, padding: "11px 14px", borderRadius: 14, fontSize: 13, fontWeight: 600, marginBottom: 16, border: `1px solid ${h.rd}25` };
    const glassPillS = { border: "none", background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, fontSize: 12.5, padding: "8px 14px", borderRadius: 999, cursor: "pointer", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", fontFamily: "inherit" };

    return (
      <div className="animate-fadeIn" style={{ minHeight: "100vh", maxWidth: 430, margin: "0 auto", background: sheetBg, display: "flex", flexDirection: "column" }}>

        {/* ── gradient top band ── */}
        <div style={{
          position: "relative", overflow: "hidden", flexShrink: 0,
          padding: "16px 20px 26px",
          background: `linear-gradient(135deg, ${ga} 0%, ${gb} 100%)`,
          borderRadius: "0 0 30px 30px", color: "#fff",
          boxShadow: `0 18px 40px ${gb}4d`, transition: "background 0.4s ease",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button onClick={() => { setRole(null); resetForm(); setMode("login"); setForgotMode(false); }} style={glassPillS}>
              {"← "}{t.back}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 800, fontSize: 13.5 }}>
                <Ic n="bldg" s={15} c="#fff" /> {t.app}
              </div>
              <button onClick={() => setDk(!dk)} aria-label="Toggle dark mode" style={{ ...glassPillS, padding: "7px 10px", display: "flex", alignItems: "center" }}>
                {dk ? <Ic n="sun" s={13} c="#fff" /> : <Ic n="moon" s={13} c="#fff" />}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 13, marginTop: 20 }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.4)", flexShrink: 0 }}>
              <Ic n={roleIcons[role] || "users"} s={26} c="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: "-0.4px" }}>{roleLabels[role]}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", fontWeight: 600, marginTop: 1 }}>
                {mode === "signup" ? (t.authCreateFast || "Create your account in under a minute") : (t.authWelcomeBack || "Welcome back — sign in to continue")}
              </div>
            </div>
          </div>

          {/* role-switch chips — glass on the gradient; active = solid white */}
          {!isAdmin && <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {["landlord", "tenant", "service"].map(r => {
              const on = r === role;
              return <button key={r}
                onClick={() => { if (!on) { setRole(r); resetForm(); setForgotMode(false); } }}
                style={{
                  border: "1px solid rgba(255,255,255,0.4)",
                  background: on ? "#fff" : "rgba(255,255,255,0.14)",
                  color: on ? roleGrads[r][1] : "#fff",
                  fontWeight: 700, fontSize: 11.5, padding: "7px 13px", borderRadius: 999,
                  cursor: "pointer", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                  transition: "background 0.25s", fontFamily: "inherit",
                }}>
                {roleLabels[r]}
              </button>;
            })}
          </div>}
        </div>

        {/* ── sheet ── */}
        <div style={{ flex: 1, padding: "22px 20px 26px" }}>
          {!isAdmin && <div style={{ display: "flex", background: dk ? "rgba(255,255,255,0.07)" : "#eceff9", borderRadius: 14, padding: 4, marginBottom: 20 }}>
            <button onClick={() => switchMode("login")} style={segS(mode === "login")}>{t.login}</button>
            <button onClick={() => switchMode("signup")} style={segS(mode === "signup")}>{t.signUp || "Sign Up"}</button>
          </div>}

          {loginErr && <div style={errBox}>{loginErr}</div>}

          {/* === LOGIN === */}
          {mode === "login" && !forgotMode && <>
            <CFld l={t.phone} v={ph} set={setPh} type="tel" ph="98765 43210" pre="+91" h={h} dk={dk} ac={gb} />
            <CFld l={t.password} v={pwd} set={setPwd} type="password" ph={t.enterPwd || "Enter password"} h={h} dk={dk} ac={gb} />
            {role !== "admin" && <div style={{ display: "flex", justifyContent: "flex-end", margin: "0 2px 18px" }}>
              <span onClick={() => { setForgotMode(true); setForgotStep(1); setForgotErr(""); setForgotInfo(""); setForgotSuccess(false); setForgotPh(ph); setForgotOtp(""); setOtpResendIn(0); }}
                style={{ color: gb, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                {t.forgotPassword || "Forgot password?"}
              </span>
            </div>}
            <button disabled={loading} onClick={handleLogin} style={{ ...ctaS, opacity: loading ? 0.7 : 1, marginTop: role === "admin" ? 6 : 0 }}>
              {loading ? "..." : `${t.signInAs || "Sign in as"} ${roleLabels[role]}`}
            </button>
          </>}

          {/* === FORGOT PASSWORD (OTP) === */}
          {mode === "login" && forgotMode && <div>
            <div onClick={() => { setForgotMode(false); setForgotErr(""); }} style={{ color: gb, fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginBottom: 16, display: "inline-flex", alignItems: "center", gap: 4 }}>
              ← {t.backToLogin || "Back to Login"}
            </div>

            <div style={{ color: h.tx, fontWeight: 800, fontSize: 16, marginBottom: 6 }}>{t.resetPassword || "Reset Password"}</div>

            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {[1, 2].map(s => <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: forgotStep >= s ? gb : lineC, transition: "background 0.3s" }} />)}
            </div>

            {forgotSuccess ? <div style={{ textAlign: "center", padding: 24 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
              <div style={{ color: h.tx, fontWeight: 800, fontSize: 15, marginBottom: 4 }}>{t.passwordReset || "Password Reset!"}</div>
              <div style={{ color: dimC, fontSize: 13, marginBottom: 18 }}>{t.canNowLogin || "You can now log in with your new password."}</div>
              <button onClick={() => { setForgotMode(false); setForgotErr(""); setForgotSuccess(false); }} style={ctaS}>{t.login}</button>
            </div> : <>
              {forgotErr && <div style={errBox}>{forgotErr}</div>}
              {forgotInfo && <div style={{ background: `${h.gn || "#16a34a"}10`, color: h.gn || "#16a34a", padding: "11px 14px", borderRadius: 14, fontSize: 13, fontWeight: 600, marginBottom: 16, border: `1px solid ${h.gn || "#16a34a"}25` }}>{forgotInfo}</div>}

              {forgotStep === 1 && <>
                <div style={{ background: `${gb}0d`, border: `1px solid ${gb}26`, borderRadius: 14, padding: 12, marginBottom: 16, fontSize: 12, color: dimC, fontWeight: 500 }}>
                  Enter the email on your account — a 6-digit code goes straight there. Valid 10 minutes. No email on your account? Sign in with your password, then add one in your profile.
                </div>
                <CFld l="Email" v={forgotPh} set={setForgotPh} type="email" ph="you@example.com" h={h} dk={dk} ac={gb} />
                <button disabled={loading} onClick={async () => {
                  const trimmed = forgotPh.trim();
                  if (!trimmed) { setForgotErr("Please enter your email"); return; }
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setForgotErr("Enter a valid email address"); return; }
                  setLoading(true); setForgotErr("");
                  const res = await api.requestPasswordResetOtp(role, trimmed);
                  setLoading(false);
                  // Server always returns 200 even for unknown phones (anti-enumeration).
                  // Move user to step 2 either way.
                  setForgotInfo(res.message || "If your account has an email on file, a 6-digit code is on its way to it. Give it a minute — check spam too.");
                  setForgotStep(2);
                  setOtpResendIn(30);  // throttle resend client-side
                }} style={{ ...ctaS, opacity: loading ? 0.7 : 1 }}>
                  {loading ? "..." : (t.sendOtp || "Send OTP")}
                </button>
              </>}

              {forgotStep === 2 && <>
                <div style={{ fontSize: 12, color: dimC, marginBottom: 12, fontWeight: 500 }}>
                  A 6-digit code was emailed to you. Enter it below. <span onClick={() => { setForgotStep(1); setForgotInfo(""); setForgotErr(""); }} style={{ color: gb, cursor: "pointer", fontWeight: 700 }}>Change</span>
                </div>
                <CFld l={t.otp || "OTP (6 digits)"} v={forgotOtp} set={setForgotOtp} type="text" ph="123456" h={h} dk={dk} ac={gb} />
                <CFld l={t.newPassword || "New Password"} v={forgotNewPwd} set={setForgotNewPwd} type="password" ph={t.enterPwd || "Min 8 characters"} h={h} dk={dk} ac={gb} />
                <CFld l={t.confirmPwd || "Confirm Password"} v={forgotConfirm} set={setForgotConfirm} type="password" ph={t.confirmPwd || "Re-enter password"} h={h} dk={dk} ac={gb} />
                <button disabled={loading} onClick={async () => {
                  const otp = (forgotOtp || "").trim();
                  if (!/^\d{6}$/.test(otp)) { setForgotErr("OTP must be exactly 6 digits"); return; }
                  if (forgotNewPwd.length < 8) { setForgotErr("Password must be at least 8 characters"); return; }
                  if (forgotNewPwd !== forgotConfirm) { setForgotErr("Passwords do not match"); return; }
                  setLoading(true); setForgotErr("");
                  const res = await api.verifyPasswordResetOtp(role, forgotPh.trim(), otp, forgotNewPwd);
                  setLoading(false);
                  if (res.success) {
                    setForgotInfo(""); setForgotSuccess(true);
                  } else {
                    setForgotErr(res.message || "Invalid or expired OTP. Please try again.");
                  }
                }} style={{ ...ctaS, opacity: loading ? 0.7 : 1 }}>
                  {loading ? "..." : (t.resetPassword || "Reset Password")}
                </button>
                {/* Resend OTP — throttled client-side; server still rate-limits */}
                <button disabled={otpResendIn > 0 || loading} onClick={async () => {
                  setForgotErr(""); setLoading(true);
                  const res = await api.requestPasswordResetOtp(role, forgotPh.trim());
                  setLoading(false);
                  setForgotInfo(res.message || "Code re-sent to your email.");
                  setOtpResendIn(30);
                  setForgotOtp("");
                }} style={{ width: "100%", marginTop: 10, padding: 14, borderRadius: 16, cursor: (otpResendIn > 0 || loading) ? "default" : "pointer", background: dk ? "rgba(255,255,255,0.05)" : "#fff", border: `1.5px solid ${lineC}`, color: h.tx, fontWeight: 700, fontSize: 14, fontFamily: "inherit", opacity: (otpResendIn > 0 || loading) ? 0.5 : 1 }}>
                  {otpResendIn > 0 ? `Resend OTP in ${otpResendIn}s` : (t.resendOtp || "Resend OTP")}
                </button>
              </>}
            </>}
          </div>}

          {/* === SIGN UP === */}
          {mode === "signup" && <>
            <CFld l={t.yourName || "Your name"} v={name} set={setName} ph={t.yourName || "Your full name"} h={h} dk={dk} ac={gb} />
            <CFld l={t.email || "Email"} v={signupEmail} set={setSignupEmail} type="email" ph="you@example.com" h={h} dk={dk} ac={gb} />
            {role === "landlord" && <CFld l={t.orgName || "Organization"} v={orgName} set={setOrgName} ph="e.g. My Rentals" h={h} dk={dk} ac={gb} />}
            {role === "service" && <CFld l={t.skill || "Skill"} v={skill} set={setSkill} ph="e.g. Plumbing, Electrical" h={h} dk={dk} ac={gb} />}
            <CFld l={t.phone} v={ph} set={setPh} type="tel" ph="98765 43210" pre="+91" h={h} dk={dk} ac={gb} />
            <CFld l={t.password} v={pwd} set={setPwd} type="password" ph={t.enterPwd || "Create password"} h={h} dk={dk} ac={gb} />
            <CFld l={t.confirmPwd || "Confirm Password"} v={confirmPwd} set={setConfirmPwd} type="password" ph={t.confirmPwd || "Confirm password"} h={h} dk={dk} ac={gb} />

            {/* Terms acceptance — must be checked before signup. The label
                text mirrors what the server records so consent language and
                UI language match exactly. */}
            <label style={{ display: "flex", gap: 9, alignItems: "flex-start", color: dimC, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, margin: "2px 2px 18px", cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: gb, marginTop: 1, cursor: "pointer", flexShrink: 0 }}
              />
              <span>
                {t.iAccept || "I have read and agree to the"}{" "}
                <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                  style={{ color: gb, fontWeight: 700, textDecoration: "underline" }}>
                  {t.termsAndConditions || "Terms & Conditions"}
                </span>
                {" "}and the{" "}
                <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPrivacyModal(true); }}
                  style={{ color: gb, fontWeight: 700, textDecoration: "underline" }}>
                  {t.privacyPolicy || "Privacy Policy"}
                </span>.
              </span>
            </label>

            <button
              disabled={loading || !termsAccepted}
              onClick={handleSignup}
              style={{ ...ctaS, opacity: (loading || !termsAccepted) ? 0.5 : 1, cursor: (loading || !termsAccepted) ? "not-allowed" : "pointer" }}>
              {loading ? "..." : (t.createAccount || "Create Account")}
            </button>
          </>}

          {/* === Google — same handlers as the landing block === */}
          {GOOGLE_CLIENT_ID && !isAdmin && !forgotMode && <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0", color: muteC, fontWeight: 700, fontSize: 10, letterSpacing: "1.6px", textTransform: "uppercase" }}>
              <div style={{ flex: 1, height: 1.5, background: lineC }} />
              {t.orContinueWith || "or continue with"}
              <div style={{ flex: 1, height: 1.5, background: lineC }} />
            </div>
            {_isCapacitorNative ? (
              <button onClick={handleNativeGoogleSignIn}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 14, borderRadius: 16, cursor: "pointer", background: dk ? "#1f1f1f" : "#fff", border: `1.5px solid ${lineC}`, color: dk ? "#e8eaed" : "#131722", fontWeight: 700, fontSize: 14, fontFamily: "inherit", boxShadow: dk ? "none" : "0 6px 18px rgba(23,30,60,0.05)" }}>
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M16.51 8.18c0-.69-.06-1.36-.18-2H9v3.78h4.21c-.18.99-.74 1.83-1.58 2.39v1.99h2.55c1.49-1.37 2.33-3.39 2.33-5.16z"/>
                  <path fill="#34A853" d="M9 17c2.13 0 3.92-.71 5.22-1.92l-2.55-1.99c-.71.48-1.61.76-2.67.76-2.06 0-3.81-1.39-4.43-3.27H1.94v2.05A8 8 0 009 17z"/>
                  <path fill="#FBBC05" d="M4.57 10.58A4.81 4.81 0 014.32 9c0-.55.09-1.09.25-1.58V5.37H1.94A8 8 0 001 9c0 1.29.31 2.51.94 3.63l2.63-2.05z"/>
                  <path fill="#EA4335" d="M9 3.58c1.16 0 2.21.4 3.03 1.18l2.27-2.27C12.91.93 11.13 0 9 0A8 8 0 001.94 5.37l2.63 2.05C5.19 4.97 6.94 3.58 9 3.58z"/>
                </svg>
                Google
              </button>
            ) : (
              <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", width: "100%" }} />
            )}
          </>}
        </div>

        {/* Terms & Conditions / Privacy modals — opened from the signup checkbox. */}
        {showTermsModal && <TermsView onClose={() => setShowTermsModal(false)} />}
        {showPrivacyModal && <PrivacyView onClose={() => setShowPrivacyModal(false)} />}
      </div>
    );
  }

  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: sp.xxl, maxWidth: 420, margin: "0 auto", background: h.bg, position: "relative", overflow: "hidden" }} className="animate-fadeIn">
    {/* Decorative blur orbs — pulled from the reference design's
        "soft luxury" aesthetic. Pinned to the viewport corners with
        position:fixed so they read as background atmosphere rather
        than content. Pointer-events:none so they never block taps.
        Skipped in dark mode where the high-blur orbs look muddy. */}
    {!dk && <>
      <div style={{
        position: "fixed", top: 0, right: 0, zIndex: 0,
        width: 380, height: 380, borderRadius: "50%",
        background: `${h.ac}14`, filter: "blur(110px)",
        transform: "translate(45%, -45%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "fixed", bottom: 0, left: 0, zIndex: 0,
        width: 420, height: 420, borderRadius: "50%",
        background: `${h.gn || h.pp || h.wn}10`, filter: "blur(130px)",
        transform: "translate(-45%, 45%)",
        pointerEvents: "none",
      }} />
    </>}

    {/* Header controls — sit ABOVE the decorative orbs */}
    <div style={{ position: "absolute", top: sp.lg, right: sp.lg, display: "flex", gap: sp.sm, zIndex: 2 }}>
      <button onClick={() => { const codes = LANGUAGES.map(l => l.code); const next = codes[(codes.indexOf(lang) + 1) % codes.length]; setLang(next); }} style={{ background: h.el || h.cd, border: `1px solid ${h.bd}`, color: h.ts || h.sc, padding: "6px 12px", borderRadius: rad.pill, cursor: "pointer", fontSize: 11, fontWeight: 600, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>{(LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]).native}</button>
      <button onClick={() => setDk(!dk)} style={{ background: h.el || h.cd, border: `1px solid ${h.bd}`, padding: "6px 10px", borderRadius: rad.pill, cursor: "pointer", display: "flex", alignItems: "center", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>{dk ? <Ic n="sun" s={14} c={h.ts || h.sc} /> : <Ic n="moon" s={14} c={h.ts || h.sc} />}</button>
    </div>

    {/* Brand mark. On the auth card (role picked) it collapses to a
        compact inline row so the role-gradient header inside the card
        becomes the hero — matches the approved concept's auth view.
        Everywhere else (org picker, Google role select) it stays the
        large centered "premium app icon" treatment. */}
    {role && !tenancies && mode !== "googleRole" ? (
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: sp.md, position: "relative", zIndex: 1 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 13,
          background: h.gl || `linear-gradient(135deg, ${h.ac} 0%, ${h.ac}dd 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 6px 18px ${h.ac}30`,
        }}><Ic n="bldg" s={20} c="#fff" /></div>
        <span style={{ color: h.tx, fontWeight: 800, fontSize: 17, letterSpacing: "-0.3px" }}>{t.app}</span>
      </div>
    ) : <>
      <div style={{
        width: 76, height: 76, borderRadius: 24,
        background: h.gl || `linear-gradient(135deg, ${h.ac} 0%, ${h.ac}dd 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: sp.lg, boxShadow: `0 12px 36px ${h.ac}35, 0 2px 8px ${h.ac}20`,
        position: "relative", zIndex: 1,
      }}><Ic n="bldg" s={36} c="#fff" /></div>

      <h1 style={{ color: h.tx, fontSize: 30, fontWeight: 800, margin: "0 0 8px", letterSpacing: "-0.6px", textAlign: "center", lineHeight: 1.1, position: "relative", zIndex: 1 }}>{t.app}</h1>
      <p style={{ color: h.ts || h.sc, fontSize: 14, margin: "0 0 32px", textAlign: "center", lineHeight: 1.45, maxWidth: 320, position: "relative", zIndex: 1 }}>{t.tag}</p>
    </>}

    {/* Org Picker — shown when tenant has multiple tenancies */}
    {tenancies ? <div style={{ width: "100%" }} className="animate-slideUp">
      <button onClick={() => { setTenancies(null); setTenancyPh(""); setTenancyPwd(""); }} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: sp.md, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}><Ic n="back" s={14} c={h.ac} /> {t.back}</button>
      <div style={{ background: h.cd, borderRadius: rad.lg, padding: sp.xxl, boxShadow: h.sh, border: `1px solid ${h.bd}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
          <div style={{ width: 40, height: 40, borderRadius: rad.md, background: `${h.ac}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ic n="users" s={20} c={h.ac} />
          </div>
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 16 }}>{t.selectOrg || "Select Organization"}</div>
            <div style={{ color: h.ts, fontSize: 12 }}>{tenancies.length} {t.tenancies || "tenancies found"}</div>
          </div>
        </div>

        {loginErr && <div style={{ background: `${h.rd}08`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginBottom: sp.lg, border: `1px solid ${h.rd}15` }}>{loginErr}</div>}

        {tenancies.map(tc => <div key={tc.tenantId}
          onClick={async () => {
            setLoading(true); setLoginErr("");
            try {
              const res = await api.selectOrg(tc.tenantId, tenancyPh, tenancyPwd);
              if (res.success) {
                localStorage.setItem("loggedIn", "true");
                localStorage.setItem("role", "tenant");
                if (res.orgId) localStorage.setItem("orgId", res.orgId);
                localStorage.setItem("currUserId", res.user.id);
                setCurrUserId(res.user.id);
                if (setOrgId && res.orgId) setOrgId(res.orgId);
                setRole("tenant");
                setLoggedIn(true);
              } else { setLoginErr(res.message || "Failed"); }
            } catch (e) { setLoginErr(e.message); }
            finally { setLoading(false); }
          }}
          style={{ display: "flex", alignItems: "center", gap: sp.md, padding: sp.lg, borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.ib, cursor: loading ? "default" : "pointer", marginBottom: sp.sm, transition: "all 0.15s", opacity: loading ? 0.6 : 1 }}>
          <div style={{ width: 36, height: 36, borderRadius: rad.sm, background: `${h.ac}10`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Ic n="building" s={18} c={h.ac} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: h.tx, fontWeight: 600, fontSize: 14 }}>{tc.orgName}</div>
            <div style={{ color: h.ts, fontSize: 12, marginTop: 2 }}>{tc.propertyAddr}</div>
          </div>
          <Ic n="chevron-right" s={16} c={h.tt} />
        </div>)}
      </div>
    </div>

    : mode === "googleRole" ? <div style={{ width: "100%" }} className="animate-slideUp">
      <button onClick={() => { setMode("login"); setRole(null); setGoogleCredential(null); setGoogleUser(null); resetForm(); }} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: sp.md, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}><Ic n="back" s={14} c={h.ac} /> {t.back}</button>
      <div style={{ background: h.cd, borderRadius: rad.lg, padding: sp.xxl, boxShadow: h.sh, border: `1px solid ${h.bd}` }}>
        {/* Google user info */}
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
          {googleUser?.avatar && <img src={googleUser.avatar} alt="" style={{ width: 40, height: 40, borderRadius: "50%" }} />}
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 16 }}>{googleUser?.name}</div>
            <div style={{ color: h.ts, fontSize: 12 }}>{googleUser?.email}</div>
          </div>
        </div>

        {!role ? <>
          <p style={{ color: h.ts, fontSize: 13, marginBottom: sp.lg, textAlign: "center" }}>{t.selectRole || "Select your role to complete signup"}</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm }}>
            {["landlord", "tenant", "service"].map(r => <button key={r} onClick={() => { setRole(r); setLoginErr(""); }} style={{ padding: "16px 8px", borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.ib, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: sp.sm }}>
              <div style={{ width: 36, height: 36, borderRadius: rad.sm, background: `${roleColors[r]}10`, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic n={roleIcons[r]} s={18} c={roleColors[r]} /></div>
              <span style={{ color: h.tx, fontWeight: 600, fontSize: 11 }}>{roleLabels[r]}</span>
            </button>)}
          </div>
        </> : <>
          <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.lg }}>
            <div style={{ width: 32, height: 32, borderRadius: rad.sm, background: `${roleColors[role]}10`, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic n={roleIcons[role]} s={16} c={roleColors[role]} /></div>
            <span style={{ color: h.tx, fontWeight: 600, fontSize: 14 }}>{roleLabels[role]}</span>
            <button onClick={() => setRole(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: h.ts, cursor: "pointer", fontSize: 11 }}>Change</button>
          </div>

          {loginErr && <div style={{ background: `${h.rd}08`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginBottom: sp.lg, border: `1px solid ${h.rd}15` }}>{loginErr}</div>}

          <Fld l={t.yourName || "Name"} v={name} set={setName} h={h} ph={googleUser?.name || "Your full name"} />
          <PhoneFld l={t.phone} v={ph} set={setPh} h={h} />
          {role === "landlord" && <Fld l={t.orgName || "Organization Name"} v={orgName} set={setOrgName} h={h} ph={"e.g. My Rentals"} />}
          {role === "service" && <Fld l={t.skill || "Skill"} v={skill} set={setSkill} h={h} ph={"e.g. Plumbing, Electrical"} />}

          {/* T&C + Privacy acceptance — required for Google signup too.
              Same component / same state as the regular signup form so
              the user only has to agree once. The server enforces this
              independently in /auth/google. */}
          <label style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "10px 4px", marginBottom: sp.sm, cursor: "pointer",
              userSelect: "none"
          }}>
              <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", accentColor: h.ac, flexShrink: 0 }}
              />
              <span style={{ color: h.tx, fontSize: 12, lineHeight: 1.5 }}>
                  {t.iAccept || "I have read and agree to the"}{" "}
                  <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                      style={{ color: h.ac, textDecoration: "underline", fontWeight: 600 }}>
                      {t.termsAndConditions || "Terms & Conditions"}
                  </span>
                  {" "}and the{" "}
                  <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPrivacyModal(true); }}
                      style={{ color: h.ac, textDecoration: "underline", fontWeight: 600 }}>
                      {t.privacyPolicy || "Privacy Policy"}
                  </span>.
              </span>
          </label>

          <button
              disabled={loading || !termsAccepted}
              onClick={() => handleGoogleSignup(role)}
              style={{ ...btnRole(role), opacity: (loading || !termsAccepted) ? 0.5 : 1, cursor: (loading || !termsAccepted) ? "not-allowed" : "pointer" }}>
            {loading ? "..." : (t.createAccount || "Create Account")}
          </button>
        </>}
      </div>
    </div>

    : !role ? <div style={{ width: "100%", position: "relative", zIndex: 1 }}>
      {/* Section label — small uppercase eyebrow above the cards */}
      <p style={{
        color: h.ts || h.sc, fontWeight: 700, marginBottom: sp.md,
        textAlign: "center", fontSize: 11, letterSpacing: "1.5px",
        textTransform: "uppercase", opacity: 0.7,
      }}>{t.selectRole}</p>

      {/* Bento-style role cards — vertical stack on mobile because
          the app's max-width is 430px (a 3-column grid here would
          give each card ~120px which is too cramped for the icon +
          description + CTA chip treatment from the reference). */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {["landlord", "tenant", "service"].map(r => {
          const accent = roleColors[r];
          return (
            <button
              key={r}
              onClick={() => { setRole(r); resetForm(); setMode("login"); }}
              style={{
                position: "relative", overflow: "hidden",
                width: "100%", padding: "18px 18px",
                borderRadius: 22,
                border: `1px solid ${dk ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.6)"}`,
                // Glass-card effect from the reference. In dark mode
                // we use a translucent dark instead so the blur still
                // separates the card from the page background.
                background: dk ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.65)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                cursor: "pointer",
                display: "flex", alignItems: "center", gap: sp.md,
                textAlign: "left",
                boxShadow: dk
                  ? "0 6px 24px rgba(0,0,0,0.35)"
                  : `0 6px 24px ${accent}10, 0 1px 3px rgba(0,0,0,0.04)`,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = dk
                  ? "0 10px 32px rgba(0,0,0,0.45)"
                  : `0 14px 36px ${accent}20, 0 2px 6px rgba(0,0,0,0.06)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = dk
                  ? "0 6px 24px rgba(0,0,0,0.35)"
                  : `0 6px 24px ${accent}10, 0 1px 3px rgba(0,0,0,0.04)`;
              }}
            >
              {/* Accent wash that fades in on hover — subtle directional
                  gradient lifts the card without changing its color. */}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                background: `linear-gradient(135deg, ${accent}10 0%, transparent 60%)`,
                opacity: 0.6,
              }} />

              {/* Icon tile — larger than the previous 44px so the role
                  reads at-a-glance, with a soft inset shadow that gives
                  it the "lifted square" feel from the reference. */}
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: dk ? `${accent}22` : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, position: "relative", zIndex: 1,
                boxShadow: dk ? "none" : `0 2px 8px ${accent}15, inset 0 0 0 1px ${accent}18`,
              }}>
                <Ic n={roleIcons[r]} s={26} c={accent} />
              </div>

              {/* Text column — name + 1-line value prop + CTA chip */}
              <div style={{ flex: 1, minWidth: 0, position: "relative", zIndex: 1 }}>
                <div style={{
                  color: h.tx, fontWeight: 700, fontSize: 17,
                  letterSpacing: "-0.2px", marginBottom: 3,
                }}>{roleLabels[r]}</div>
                <div style={{
                  color: h.ts || h.sc, fontSize: 12, lineHeight: 1.4,
                  marginBottom: 8,
                }}>{roleDescriptions[r]}</div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  color: accent, fontSize: 10, fontWeight: 700,
                  letterSpacing: "1px", textTransform: "uppercase",
                }}>
                  <span>{roleCTAs[r]}</span>
                  <span style={{ fontSize: 12, lineHeight: 1 }}>{"›"}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Super Admin — kept secondary so it doesn't compete with the
          three primary role cards above. Compact pill in admin-purple. */}
      <button
        onClick={() => { setRole("admin"); resetForm(); setMode("login"); }}
        style={{
          width: "100%", marginTop: sp.md,
          padding: "10px 14px", borderRadius: rad.pill,
          border: `1px dashed ${(h.pp || h.wn)}40`,
          background: dk ? "transparent" : `${(h.pp || h.wn)}06`,
          cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", gap: 6,
          transition: "background 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${(h.pp || h.wn)}12`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = dk ? "transparent" : `${(h.pp || h.wn)}06`; }}
      >
        <Ic n="gear" s={12} c={h.pp || h.wn} />
        <span style={{ color: h.pp || h.wn, fontWeight: 700, fontSize: 11, letterSpacing: "0.3px" }}>{t.superAdmin || "Super Admin"}</span>
      </button>

      {/* Google Sign-In */}
      {GOOGLE_CLIENT_ID && <>
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, margin: "24px 0 14px" }}>
          <div style={{ flex: 1, height: 1, background: h.bd }} />
          <span style={{ color: h.tt || h.sc, fontSize: 11, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>{t.orContinueWith || "or"}</span>
          <div style={{ flex: 1, height: 1, background: h.bd }} />
        </div>
        {googleLoading ? (
          <div style={{ textAlign: "center", padding: sp.lg }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
          </div>
        ) : _isCapacitorNative ? (
          // Native Capacitor: GIS won't render in a WebView, so we draw
          // our own button styled to match Google's brand and trigger the
          // native account picker via the Capacitor plugin.
          <button
            onClick={handleNativeGoogleSignIn}
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 24,
              border: `1px solid ${h.bd}`, background: dk ? "#1f1f1f" : "#fff",
              color: dk ? "#e8eaed" : "#3c4043", fontSize: 14, fontWeight: 500,
              cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", gap: 12,
              boxShadow: dk ? "none" : "0 1px 2px rgba(0,0,0,0.1)",
              transition: "all 0.15s ease"
            }}>
            {/* Google "G" logo — official 4-color SVG */}
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M16.51 8.18c0-.69-.06-1.36-.18-2H9v3.78h4.21c-.18.99-.74 1.83-1.58 2.39v1.99h2.55c1.49-1.37 2.33-3.39 2.33-5.16z"/>
              <path fill="#34A853" d="M9 17c2.13 0 3.92-.71 5.22-1.92l-2.55-1.99c-.71.48-1.61.76-2.67.76-2.06 0-3.81-1.39-4.43-3.27H1.94v2.05A8 8 0 009 17z"/>
              <path fill="#FBBC05" d="M4.57 10.58A4.81 4.81 0 014.32 9c0-.55.09-1.09.25-1.58V5.37H1.94A8 8 0 001 9c0 1.29.31 2.51.94 3.63l2.63-2.05z"/>
              <path fill="#EA4335" d="M9 3.58c1.16 0 2.21.4 3.03 1.18l2.27-2.27C12.91.93 11.13 0 9 0A8 8 0 001.94 5.37l2.63 2.05C5.19 4.97 6.94 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
        ) : (
          <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", width: "100%" }} />
        )}
        {loginErr && <div style={{ background: `${h.rd}10`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginTop: sp.md, textAlign: "center", border: `1px solid ${h.rd}20` }}>{loginErr}</div>}
      </>}
    </div>

    : null}

    {/* Browse Properties — glass pill matching the role cards above
        so the bottom of the page reads as one consistent surface
        family rather than a card stack + an out-of-place button. */}
    <button
      onClick={() => setPublicPage("browse")}
      style={{
        marginTop: sp.lg, width: "100%",
        padding: "13px 20px", borderRadius: 18,
        border: `1px solid ${dk ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.6)"}`,
        background: dk ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        color: h.tx, cursor: "pointer",
        fontSize: 14, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center", gap: sp.sm,
        boxShadow: dk ? "0 4px 16px rgba(0,0,0,0.3)" : `0 4px 16px ${h.ac}10`,
        transition: "all 0.2s ease",
        position: "relative", zIndex: 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <Ic n="bldg" s={16} c={h.ac} />{t.browseProperties || "Browse Properties"}
    </button>
    <p style={{ color: h.tt || h.sc, fontSize: 10, marginTop: sp.lg, letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 600, opacity: 0.6, position: "relative", zIndex: 1 }}>Version 4.0</p>

    {/* Terms & Conditions modal — opened from the signup checkbox. */}
    {showTermsModal && <TermsView onClose={() => setShowTermsModal(false)} />}
    {/* Privacy Policy modal — opened from the signup checkbox. */}
    {showPrivacyModal && <PrivacyView onClose={() => setShowPrivacyModal(false)} />}
  </div>;
}
